import React, { useEffect, useRef } from 'react';
import HomeHeader from '@/components/HomeHeader';
import Footer from '@/components/homepage/Footer';
import { usePageMeta } from '@/hooks/usePageMeta';
import TableOfContents from '@/components/resources/TableOfContents';
import EmailCapture from '@/components/resources/EmailCapture';

const TITLE = 'Sending Agency vs. Going Independent: How Missionaries Should Choose';
const DESCRIPTION =
    "Going through a sending agency and raising support independently solve different problems. A practical look at what each actually gives you — accountability, cost, speed, and control — so you can pick with your eyes open.";

const JSON_LD = {
    '@context': 'https://schema.org',
    '@graph': [
        {
            '@type': 'Article',
            headline: TITLE,
            description: DESCRIPTION,
            image: 'https://devotionfund.com/resources/cover22.jpg',
            datePublished: '2026-08-07',
            dateModified: '2026-08-07',
            author: { '@type': 'Organization', name: 'Devotion Fund', url: 'https://devotionfund.com' },
            publisher: {
                '@type': 'Organization',
                name: 'Devotion Fund',
                logo: { '@type': 'ImageObject', url: 'https://devotionfund.com/logo.png' },
            },
            mainEntityOfPage: {
                '@id': 'https://devotionfund.com/resources/sending-agency-vs-independent-missionary',
                '@type': 'WebPage',
            },
        },
        {
            '@type': 'FAQPage',
            mainEntity: [
                {
                    '@type': 'Question',
                    name: 'Is it legitimate to raise support without a sending agency?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "Yes. Going independent means you're personally responsible for your own accountability, field logistics, and legal structure instead of an agency handling them, but it's a well-established path — many missionaries raise support directly, especially for shorter terms or roles connected to an existing local church or ministry partner.",
                    },
                },
                {
                    '@type': 'Question',
                    name: 'Do I lose tax-deductibility by going independent?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "Usually, yes — a 501(c)(3) sending agency is what typically makes gifts to you tax-deductible for US donors. Giving directly to an individual, through a platform or otherwise, is usually a personal gift rather than a deductible charitable donation. That's a real trade-off to discuss with donors, not a footnote.",
                    },
                },
                {
                    '@type': 'Question',
                    name: 'What does a sending agency actually take in fees?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "It varies widely by agency, but administrative fees commonly run somewhere in the 8-15% range of funds raised, on top of any home-office assessment or benefits cost built into your support goal. Ask for the exact percentage and what it covers before you commit — agencies differ a lot on this.",
                    },
                },
                {
                    '@type': 'Question',
                    name: 'Can I switch from an agency to independent, or the other way around?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "It happens, but it's disruptive — donors gave to a specific structure and may need to be re-recruited or re-confirmed under the new one, and any tax-deductibility status can change. It's worth choosing deliberately up front rather than treating it as easily reversible.",
                    },
                },
            ],
        },
    ],
};

export default function SendingAgencyVsIndependentMissionary() {
    usePageMeta(TITLE, DESCRIPTION, { path: '/resources/sending-agency-vs-independent-missionary' });

    const articleRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const el = document.createElement('script');
        el.type = 'application/ld+json';
        el.setAttribute('data-agency-vs-independent-jsonld', 'true');
        el.textContent = JSON.stringify(JSON_LD);
        document.head.appendChild(el);
        return () => {
            el.remove();
        };
    }, []);

    return (
        <div className="min-h-screen bg-white flex flex-col">
            <HomeHeader />
            <div className="w-full h-64 md:h-80 bg-gray-100 overflow-hidden">
                <img src="/resources/cover22.jpg" alt="A missionary weighing two paths at a desk with paperwork from a sending agency" className="w-full h-full object-cover" />
            </div>
            <div className="max-w-3xl mx-auto px-4 py-12 w-full">
                <h1 className="text-3xl font-bold mb-6">{TITLE}</h1>
                <TableOfContents targetRef={articleRef} />
                <div className="prose max-w-none" ref={articleRef}>
                    <p>Almost every missionary hits this question early: go through a sending agency, or raise support independently. It gets framed as a spiritual or organizational decision, but underneath it's mostly practical — who handles accountability, who takes a cut and for what, how fast you can get to the field, and how much control you keep over your own budget and donor relationships. Neither path is automatically right; they solve different problems.</p>

                    <h2>What a sending agency actually gives you</h2>

                    <p>A good agency isn't just a fee-taking middleman — it typically provides tax-deductible giving status for US donors (through its 501(c)(3) status), a field-tested accountability structure, pre-field training, member care and crisis support if something goes wrong overseas, and often a built-in community of other missionaries in similar roles or regions. For a first-term missionary heading somewhere logistically or spiritually difficult, that infrastructure is worth a real look — it exists because people needed it.</p>

                    <h2>What it costs you</h2>

                    <p>That infrastructure isn't free. Administrative fees commonly land somewhere in the 8-15% range of funds raised, sometimes layered with a separate home-office assessment or required benefits package that raises your actual support goal above your field budget. You'll also typically work within the agency's approved fundraising methods, reporting requirements, and sometimes a required minimum support level before departure — all reasonable, but all constraints on how you operate.</p>

                    <h2>What going independent actually gives you</h2>

                    <p>Raising support independently — often through a local church, an established ministry partnership, or a direct-giving platform — usually means faster setup (no agency application and approval cycle), full visibility into your own numbers, direct ownership of your donor relationships instead of routing through an agency's database, and no administrative percentage taken off the top. It suits missionaries with a clear sending church already invested in them, shorter-term or bi-vocational roles, or anyone who has already done cross-cultural ministry and doesn't need the training layer.</p>

                    <h2>What it costs you</h2>

                    <p>Independence usually means gifts to you are personal support rather than tax-deductible donations — see <a href="/resources/is-missionary-support-tax-deductible">is missionary support tax-deductible</a> for the specifics — which is a real conversation to have with donors up front, not something to gloss over. You're also on your own for accountability structure, and you carry more of the practical load: budgeting, reporting, and building your own giving infrastructure. Tools like a <a href="/resources/how-to-start-monthly-giving-page-without-501c3">monthly giving page without a 501(c)(3)</a> exist specifically to make that load manageable.</p>

                    <h2>The question that actually decides it</h2>

                    <p>Skip "which is more legitimate" — both are. Ask instead: do your likely donors care about tax-deductibility enough that losing it would shrink your base, and does your role benefit from an agency's training, member care, and field infrastructure, or do you already have that support through a sending church or existing ministry partner? A first-time missionary heading somewhere unfamiliar with no existing network usually leans agency. A missionary sent by a strong home church, doing a defined-length assignment, or already experienced in the field often finds independent support raising faster and more direct — see <a href="/resources/how-to-ask-your-church-to-support-you-as-a-missionary">how to ask your church to support you</a> for how that relationship typically starts.</p>

                    <h2>A hybrid worth knowing about</h2>

                    <p>Some missionaries combine both: fiscal sponsorship through an established ministry gives donors tax-deductibility while the missionary keeps day-to-day autonomy over the campaign and relationships. It's a middle path, not a loophole, and it's worth researching if the deductibility question is the main thing holding you back from going independent.</p>

                    <h2>Frequently asked questions</h2>

                    <p><strong>Is it legitimate to raise support without a sending agency?</strong><br />
                    Yes — it's a well-established path, especially with a strong local church or ministry partner behind you.</p>

                    <p><strong>Do I lose tax-deductibility by going independent?</strong><br />
                    Usually. Gifts become personal support rather than deductible donations, which is worth discussing with donors directly.</p>

                    <p><strong>What does a sending agency actually take in fees?</strong><br />
                    Commonly 8-15% of funds raised, sometimes with an added home-office assessment — ask each agency for its exact number.</p>

                    <p><strong>Can I switch structures later?</strong><br />
                    It's possible but disruptive, since donors gave under a specific structure. Choose deliberately rather than assuming an easy switch.</p>

                    <h2>Next reads on Devotion Fund</h2>
                    <ul>
                        <li><a href="/resources/is-missionary-support-tax-deductible">Is Missionary Support Tax-Deductible?</a></li>
                        <li><a href="/resources/how-to-start-monthly-giving-page-without-501c3">How to Start a Monthly Giving Page Without a 501(c)(3)</a></li>
                        <li><a href="/resources/how-to-ask-your-church-to-support-you-as-a-missionary">How to Ask Your Church to Support You as a Missionary</a></li>
                    </ul>

                    <h2>About the author</h2>
                    <p>We're the team behind <a href="/">Devotion Fund</a>. We built a direct, recurring-first platform for the independent path specifically — but the right structure depends on your situation, not on which option we happen to serve.</p>
                    <p><small><em>Last updated: 2026-08-07.</em></small></p>
                </div>
                <div className="mt-8">
                    <EmailCapture page="/resources/sending-agency-vs-independent-missionary" />
                </div>
            </div>
            <Footer />
        </div>
    );
}
